import { useEffect, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { InfoCircledIcon, UpdateIcon } from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CopyButton } from '@/components/shared/copy-button'
import { Icons } from '@/components/shared/icons'

/**
 * "Generated blueprint" panel: shows the STORED, pre-rendered blueprint PNG
 * next to the live Konva preview, so an admin can spot a cached image that
 * has drifted from what the catalogue currently draws.
 *
 * Full contract as landed in
 * `furnisystems-backend/src/types-modified/SofaFormBlueprint.ts` (re-read
 * 2026-09-11 against the real file, not a summary):
 *   SofaForm.blueprint: SofaFormBlueprint  -- nullable only if the SofaForm
 *     row itself couldn't be read; NEVER null merely because nothing has been
 *     rendered (that's status: not_requested).
 *   SofaFormBlueprint { status! url thumbnail_url error hash! width height
 *     thumb_width thumb_height clipped! warnings! }
 *   renderBlueprintForSofaForm(id: Int!, force: Boolean = false): SofaFormBlueprint!
 *     -- admin-only (rules.isAdminUser), gated behind BLUEPRINT_RENDER_ENABLED
 *     (default OFF), which throws before touching the DB when disabled.
 *
 * `force` only means something when status is already `rendered` (elsewhere
 * it's a no-op on the backend), and a FORCED re-render of a `rendered` row
 * deliberately returns an UNCHANGED `rendered` object -- the row is left as
 * "rendered" throughout the re-render so the pricelist/table never sees a
 * blank image (see the mutation's doc comment). So there is no status
 * transition to hang a "regenerating" state on for that one case; success is
 * acknowledged with a transient banner instead (`justQueued`).
 */

type BlueprintStatus = 'pending' | 'rendered' | 'failed' | 'not_requested'

interface SofaFormBlueprintData {
  status: BlueprintStatus
  url?: string | null
  thumbnail_url?: string | null
  error?: string | null
  hash: string
  width?: number | null
  height?: number | null
  thumb_width?: number | null
  thumb_height?: number | null
  clipped: boolean
  warnings: string[]
}

interface GeneratedBlueprintPanelProps {
  /** SofaForm.id -- the only variable renderBlueprintForSofaForm needs. */
  sofaFormId: number
  /** SofaForm.blueprint from the details query. */
  blueprint?: SofaFormBlueprintData | null
  /** True while the query backing `blueprint` is still in flight. */
  loading?: boolean
}

export const RENDER_BLUEPRINT_FOR_SOFA_FORM = gql`
  mutation RenderBlueprintForSofaForm($id: Int!, $force: Boolean) {
    renderBlueprintForSofaForm(id: $id, force: $force) {
      status
      url
      thumbnail_url
      error
      hash
      width
      height
      thumb_width
      thumb_height
      clipped
      warnings
    }
  }
`

/** Fixed so the image area never jumps size between statuses. */
const FRAME_HEIGHT = 'h-56'

/**
 * Exported so other places that render stored blueprint PNGs (e.g. the
 * Modules table and the pricelist Sofa Modules table) use the exact same
 * transparency backdrop instead of inventing a second visual language.
 */
export const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), ' +
    'linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, #e5e7eb 75%), ' +
    'linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: '#ffffff',
}

const STATUS_BADGE: Record<
  BlueprintStatus,
  { label: string; variant: BadgeProps['variant'] }
> = {
  rendered: { label: 'Rendered', variant: 'green' },
  pending: { label: 'Queued', variant: 'lightBlue' },
  failed: { label: 'Failed', variant: 'destructive' },
  not_requested: { label: 'No image yet', variant: 'outline' },
}

/** Best-effort message out of an Apollo mutation rejection. */
function extractErrorMessage(error: unknown): string {
  const anyErr = error as any
  return (
    anyErr?.graphQLErrors?.[0]?.message ||
    anyErr?.networkError?.message ||
    anyErr?.message ||
    'The request failed for an unknown reason.'
  )
}

export default function GeneratedBlueprintPanel({
  sofaFormId,
  blueprint,
  loading,
}: GeneratedBlueprintPanelProps) {
  // Mutation responses are applied straight over the query's value rather
  // than refetched (see file header) -- this is the whole SofaFormBlueprint
  // object returned by renderBlueprintForSofaForm.
  //
  // `src/containers/Layout/ContentDrawer.tsx` renders
  // `{data && Component && React.createElement(Component, {data, ...})}` for
  // a single Sheet instance, and CLOSE_DRAWER nulls `drawerComponent` before
  // any subsequent OPEN_DRAWER -- so the normal open -> close -> open-again
  // flow unmounts this component between edits (confirmed by reading that
  // file, not assumed). But the Sheet is `modal`, not a structural guarantee:
  // nothing stops a future code path from dispatching OPEN_DRAWER a second
  // time with new `data` while already open (same `Component` identity =
  // React would treat it as a prop update on the SAME instance, not a
  // remount). So this override is reset explicitly whenever `sofaFormId`
  // changes, rather than relying on the drawer lifecycle alone.
  const [override, setOverride] = useState<SofaFormBlueprintData | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  // Acknowledges a forced re-render of an already-`rendered` blueprint,
  // which by design returns the SAME status -- there is no state change to
  // show otherwise. Auto-clears; see the effect below.
  const [justQueued, setJustQueued] = useState(false)

  const [renderBlueprint, { loading: isMutating }] = useMutation(
    RENDER_BLUEPRINT_FOR_SOFA_FORM
  )

  useEffect(() => {
    setOverride(null)
    setMutationError(null)
    setJustQueued(false)
  }, [sofaFormId])

  useEffect(() => {
    if (!justQueued) return
    const timer = setTimeout(() => setJustQueued(false), 4000)
    return () => clearTimeout(timer)
  }, [justQueued])

  const effective = override ?? blueprint ?? null
  const status = effective?.status

  const handleGenerate = async (force: boolean) => {
    setMutationError(null)
    setJustQueued(false)
    try {
      const { data } = await renderBlueprint({
        variables: { id: sofaFormId, force },
      })
      const result = data?.renderBlueprintForSofaForm as
        | SofaFormBlueprintData
        | undefined
      if (result) {
        setOverride(result)
        if (force && result.status === 'rendered') {
          setJustQueued(true)
        }
      }
    } catch (error) {
      // Catch and show inline -- this is expected to fire on any machine
      // where BLUEPRINT_RENDER_ENABLED is off (the default): the mutation
      // refuses before touching the DB and names the flag in its message.
      // That is correct, informative behaviour, not a crash or a generic
      // failure toast.
      setMutationError(extractErrorMessage(error))
    }
  }

  const displayUrl = effective?.thumbnail_url || effective?.url || null
  const hasImage = status === 'rendered' && !!displayUrl
  const hasCaution =
    status === 'rendered' && (effective?.clipped || (effective?.warnings?.length ?? 0) > 0)

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`relative w-full ${FRAME_HEIGHT} rounded-md border overflow-hidden flex items-center justify-center`}
        style={hasImage || loading ? CHECKERBOARD_STYLE : undefined}
      >
        {loading ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : hasImage ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={displayUrl!}
            alt="Stored blueprint render"
            className="max-h-full max-w-full object-contain"
          />
        ) : status === 'pending' ? (
          <div className="flex flex-col items-center gap-1 px-4 text-center text-muted-foreground">
            <UpdateIcon className="h-5 w-5 animate-spin" />
            <p className="text-sm font-medium">Queued for render</p>
            <p className="text-xs">Come back shortly -- this is not an error.</p>
          </div>
        ) : status === 'failed' ? (
          <div className="flex flex-col items-center gap-1 px-4 text-center text-destructive">
            <Icons.warning className="h-5 w-5" />
            <p className="text-sm font-medium">Render failed</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 px-4 text-center text-muted-foreground">
            <InfoCircledIcon className="h-5 w-5" />
            <p className="text-sm font-medium">No image yet</p>
            <p className="text-xs">This module has not been rendered.</p>
          </div>
        )}
      </div>

      {!loading && status && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_BADGE[status].variant}>
                {STATUS_BADGE[status].label}
              </Badge>
              {(effective?.width || effective?.height) && (
                <span className="text-muted-foreground">
                  {effective?.width ?? '?'}&nbsp;&times;&nbsp;{effective?.height ?? '?'}px
                  {(effective?.thumb_width || effective?.thumb_height) && (
                    <>
                      {' '}
                      (thumb {effective?.thumb_width ?? '?'}&nbsp;&times;&nbsp;
                      {effective?.thumb_height ?? '?'}px)
                    </>
                  )}
                </span>
              )}
            </div>
            {effective?.url && (
              <a
                href={effective.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Open full-size image
              </a>
            )}
          </div>

          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>Hash:</span>
            <code className="rounded bg-muted px-1 py-0.5" title={effective!.hash}>
              {effective!.hash.slice(0, 12)}&hellip;
            </code>
            <CopyButton
              value={effective!.hash}
              className="h-6 w-6 shrink-0 border-none bg-transparent p-1 text-muted-foreground hover:text-foreground dark:text-muted-foreground"
            />
          </div>

          {status === 'failed' && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
              {effective?.error || 'Failed -- no further details available.'}
            </div>
          )}

          {hasCaution && (
            <div className="flex flex-col gap-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              {effective?.clipped && (
                <span className="flex items-center gap-1">
                  <Icons.warning className="h-3.5 w-3.5 shrink-0" />
                  Image was clipped to the renderer&apos;s size ceiling and is
                  no longer exactly to scale.
                </span>
              )}
              {(effective?.warnings?.length ?? 0) > 0 && (
                <span className="flex items-start gap-1">
                  <Icons.warning className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{effective!.warnings.join('; ')}</span>
                </span>
              )}
            </div>
          )}

          {justQueued && (
            <div className="rounded-md border border-green-300 bg-green-50 p-2 text-xs text-green-800">
              Queued for re-render. The existing image stays visible until the
              new one is ready.
            </div>
          )}

          {mutationError && (
            <div className="flex items-start gap-1.5 rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
              <InfoCircledIcon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{mutationError}</span>
            </div>
          )}

          {status !== 'pending' && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMutating}
                onClick={() => handleGenerate(status === 'rendered')}
              >
                {isMutating && <UpdateIcon className="h-4 w-4 animate-spin" />}
                {isMutating
                  ? 'Requesting...'
                  : status === 'rendered'
                    ? 'Re-generate'
                    : 'Generate'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
